import React, { FunctionComponent } from 'react';
import { useMediaQuery } from 'react-responsive';
import { Table } from '../../UI/V3/Table/Table';
import { TableHeader } from '../../UI/V3/Table/Header/TableHeader';
import { TableHeaderCell } from '../../UI/V3/Table/HeaderCell/TableHeaderCell';
import { TableBody } from '../../UI/V3/Table/Body/TableBody';
import DefaultTableEmptyState from '../../UI/V3/Table/EmptyState/DefaultTableEmptyState';
import { Spinner } from '../../UI/V3/Spinner/Spinner';
import { useOrganizationsContext } from '../OrganizationsProvider';
import styles from './OrganizationsTable.module.scss';
import OrganizationRow from './OrganizationRow';

interface OrganizationsTableProps {
    className?: string;
}

export const OrganizationsTable: FunctionComponent<OrganizationsTableProps> = ({ className }) => {
    const { filteredParentAccounts, filteredChildAccounts, loading } = useOrganizationsContext();

    const isMobile = useMediaQuery({ query: '(max-width:768px)' });

    // Get the root account (parentOrganizationId = null)
    const rootAccount = filteredParentAccounts[0];

    // Get direct children of root account to display as top-level items
    const topLevelAccounts = rootAccount
        ? filteredChildAccounts.filter(account => account.parentOrganizationId === rootAccount.id)
        : [];

    const hasInitialized = true;
    const isEmpty = topLevelAccounts.length === 0 && !loading;

    // RENDER DIFFERENT TABLE BODIES BASED ON STATE
    const renderTableContent = () => {
        // Show spinner in TableBody when:
        // 1. First load (no data yet)
        // 2. Loading with no current data to show (empty results + loading)
        const shouldShowSpinnerInBody =
            loading &&
            (!hasInitialized || // First time loading
                topLevelAccounts.length === 0); // Currently no data to show while loading

        if (shouldShowSpinnerInBody) {
            return (
                <TableBody>
                    <tr>
                        <td
                            colSpan={isMobile ? 2 : 7}
                            style={{ textAlign: 'center', padding: '40px' }}
                        >
                            <Spinner />
                        </td>
                    </tr>
                </TableBody>
            );
        }

        // No results for current filters/search
        if (isEmpty) {
            return (
                <TableBody className={styles.tableBodyEmpty}>
                    <DefaultTableEmptyState
                        title="No accounts found"
                        description={[
                            'No accounts match your current search criteria.',
                            'Try adjusting your filters or search terms.'
                        ]}
                    />
                </TableBody>
            );
        }

        // Normal data
        return (
            <TableBody>
                {topLevelAccounts.map((account, index) => {
                    return (
                        <OrganizationRow
                            key={account.id}
                            account={account}
                            allChildAccounts={filteredChildAccounts}
                            isLast={topLevelAccounts.length === index + 1}
                            isMobile={isMobile}
                            depth={0}
                        />
                    );
                })}
            </TableBody>
        );
    };

    return (
        <Table
            className={`${className || ''} ${styles.table}`}
            fullHeight={!isMobile}
        >
            <TableHeader className={styles.tableHeader}>
                <TableHeaderCell>Nickname</TableHeaderCell>
                <TableHeaderCell>Type</TableHeaderCell>
                <TableHeaderCell>City, State</TableHeaderCell>
                <TableHeaderCell>Locations</TableHeaderCell>
                <TableHeaderCell></TableHeaderCell>
            </TableHeader>
            {renderTableContent()}
        </Table>
    );
};

export default OrganizationsTable;
